// Loaded-strategy self-heal: strip vestigial retired `data.code` (pure; browser + node safe).
//
// A custom block that ALREADY runs via a `parameters.__canonical` marker (trusted-TS
// dispatch) or a `tslAst` (the TSL interpreter) ignores any `data.code` entirely — the
// engine + validator never read it (see ClientSideBacktestEngine + strategyValidation:
// marker/AST blocks dispatch without host code; strategy-JS-zero proves code never runs).
// But such a block that kept BOTH a marker/AST AND leftover `data.code` is still counted
// as "legacy code-bearing", which re-surfaces the red "Migration available … canonical
// blocks … carry retired code, clean them up" banner on every load even though there is
// nothing for the user to do. Strip that dead code, silently, on the way into the Builder.
//
// Invariants (regression-tested in scripts/test-vestigial-code-heal.mjs):
//   - removes ONLY `data.code`, and ONLY from blocks that already run (marker OR tslAst);
//   - preserves id / type / position / connections / parameters (incl. __canonical) /
//     tslAst / name / provenance — so semantic params + strategy execution are unchanged;
//   - leaves genuinely-quarantined blocks (code WITHOUT a marker AND without a tslAst)
//     untouched, so their real migration prompt still shows;
//   - idempotent: returns the SAME array reference when nothing changed (no spurious
//     re-render, no re-strip on repeated loads).
// Behaviour-preserving by construction; it persists clean on the next Save / Update.

import type { Block } from '../../types';

export function healVestigialLegacyCode(blocks: Block[]): Block[] {
  if (!Array.isArray(blocks)) return blocks;
  let changed = false;
  const next = blocks.map((b) => {
    if (b?.type !== 'custom') return b;
    const d = (b.data ?? {}) as { code?: unknown; tslAst?: unknown; parameters?: { __canonical?: unknown } };
    const hasCode = typeof d.code === 'string' && d.code.trim().length > 0;
    if (!hasCode) return b;
    const runsWithoutCode = typeof d.parameters?.__canonical === 'string' || (d.tslAst != null && typeof d.tslAst === 'object');
    if (!runsWithoutCode) return b; // genuinely-quarantined → leave the real prompt intact
    changed = true;
    const data = { ...(b.data as Record<string, unknown>) };
    delete (data as { code?: unknown }).code;
    return { ...b, data } as Block;
  });
  return changed ? next : blocks;
}
